import React, {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { register, isLoggedIn, isRegister, userToken, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import {Trans,  useTranslation } from 'react-i18next';
import {apiErrorMessage, checkApiSessionErrorMessage, checkValidToken, getLang, isValidIsraeliIdNumber} from "../../Helper"

import Frame from '../widgets/Frame';
import FormErrors from '../widgets/FormErrors';
import { isFeatureOn, tBrand } from "../../portalSettings";
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useCaptcha } from "../../captcha";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

/**
 * סוגי הזיהוי שאפשר להירשם בהם, והמתג בקטלוג שפותח כל אחד.
 *
 * **הערכים הם בדיוק אלה של `customers.valid_id_type`** - `ENUM('ID','PASSPORT','COM')` -
 * ולא תוויות תצוגה. עד כאן המסך שלח `'Passport'` ו-`'Company'`, שאינם ערכים בעמודה:
 * הם עברו את ה-SSO (שלא אימת את הערך) והגיעו ל-`ENUM`, שם הם נשמרים כמחרוזת ריקה -
 * כלומר לקוח בלי סוג זיהוי, שגם חיפוש הכפילות אינו מוצא.
 *
 * `checkDigit` הוא האם למספר יש ספרת ביקורת שאפשר לבדוק. לדרכון אין - כל מספר הוא
 * דרכון תקין - וזו הסיבה שהוא כבוי בברירת המחדל של הקטלוג בבילינג.
 *
 * `feature` הוא המתג ברמת הריסיילר. **המסך אינו השער**: שתי שכבות בשרת דוחות סוג
 * שנסגר (ה-SSO לפי ה-Host, והבילינג לפי שורת הריסיילר שהלקוח נוצר תחתיה), וכאן
 * נקבע רק מה מוצע.
 */
const ID_TYPES = [
    // `label` הוא הכיתוב בשדה המספר, ו-`pickerLabel` הוא הכיתוב על הלחצן בבורר.
    // שניים ולא אחד, כי הם אינם אותו טקסט: "מספר זהות (9 ספרות)" הוא הנחיה למי
    // שממלא, ו"תעודת זהות" הוא שם של אפשרות לבחור בה.
    {value: 'ID', label: 'ID number', pickerLabel: 'ID_TYPE_ID', feature: 'allowIdTypeId', checkDigit: true},
    {value: 'COM', label: 'Company number', pickerLabel: 'ID_TYPE_COM', feature: 'allowIdTypeCompany', checkDigit: true},
    {value: 'PASSPORT', label: 'Passport number', pickerLabel: 'ID_TYPE_PASSPORT', feature: 'allowIdTypePassport', checkDigit: false},
];

const ID_TYPE_BY_VALUE = Object.fromEntries(ID_TYPES.map((type) => [type.value, type]));

export function Register() {
    const [state, setState] = useState({firstName:"", lastName:"", idType:"ID", idNumber:"", phoneCode:"+972", phoneNumber:"", communicationPref:"SMS", email: "", errors:{}, showPhoneCodeDropdown: false });

    const reseller = useSelector((state) => {
        return state.reseller.value
    });

    /**
     * הסוגים שהריסיילר פתח.
     *
     * `isFeatureOn` עם ברירת המחדל של הקטלוג לכל סוג: ת.ז. וח.פ. פתוחים, דרכון לא.
     * הגדרה שלא הגיעה - ריסיילר שאינו בתמונה - נקראת כברירת המחדל ולא כ"סגור", אותו
     * כלל שכתוב בראש `portalSettings.js`.
     *
     * **רשימה ריקה אפשרית** (ריסיילר שסגר את שלושתם), והמסך אומר זאת במקום להציג
     * טופס שלא ניתן לשלוח.
     */
    const allowedIdTypes = ID_TYPES.filter(
        (type) => isFeatureOn(reseller, type.feature, type.value !== 'PASSPORT')
    );

    /**
     * הבורר מוצג רק כשיש בין מה לבחור.
     *
     * ריסיילר שפתח סוג אחד - למשל ת.ז. בלבד - אינו צריך בורר: הוא היה תפריט עם
     * אפשרות אחת, וכל מה שהוא מוסיף הוא שאלה שאין לה שתי תשובות.
     */
    const showIdTypePicker = allowedIdTypes.length > 1;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Registration creates an account: a double click is a duplicate attempt.
    const submit = useSubmitGuard();
    /**
     * הקאפצ'ה. פעילה רק כשההגדרה הגיעה מהשרת, ומה שנשלח נבדק שם בלי קשר למסך.
     */
    const captcha = useCaptcha('register');

    const isUserLoggedIn = useSelector(isLoggedIn);
    const isUserRegister = useSelector(isRegister);

    /**
     * הסוג שבתוקף: מה שנבחר, אם הוא פתוח, ואחרת הראשון שפתוח.
     *
     * נגזר ואינו נכתב ל-state, וזו הסיבה: ההגדרות מגיעות מהשרת **אחרי** שהמסך עלה
     * (`fetchReseller`), ו-`state.idType` נולד כ-`'ID'`. ריסיילר שסגר את ת.ז. היה
     * מקבל טופס שהסוג שנשלח בו אינו פתוח - שגיאה שנראית ללקוח כמו תקלה בשרת.
     * `useEffect` שהיה מתקן את ה-state היה מוחק בחירה שהלקוח בדיוק עשה.
     */
    const effectiveIdType =
        allowedIdTypes.some((type) => type.value === state.idType)
            ? state.idType
            : (allowedIdTypes[0]?.value ?? 'ID');


    const phoneCodes = {'+972':'israel', '+1':'usa', '+44':'uk', '+33':'france', '+52':'mexico', '+54':'argentina', '+56':'chile', '+971':'uae', '+47':'norway'}

    useEffect(()=>{
        if(isUserLoggedIn){
            checkValidToken(navigate, dispatch, logout);

            /*if(searchParams.get("redirect")) {
                window.location.replace(searchParams.get("redirect"));
            } else {
                navigate('/successfully-logged-in');
            }*/
        }
        if(isUserRegister){
            getRegistrationData();
        }
        // eslint-disable-next-line
    },[]);

    const getRegistrationData = async (e) => {
        let errorMessage = DEFAULT_ERROR_TEXT;

        let config = isUserRegister?{'headers': {"token": userToken(), "lang": getLang()}}:{'headers': {"lang": getLang()}};
        try {
            const reply = await axios.get(API_URL + 'getRegistrationData', config);
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK) {
                setState({
                    ...state,
                    firstName: reply.data.data.firstName,
                    lastName: reply.data.data.lastName,
                    email: reply.data.data.email,
                    idNumber: ""+reply.data.data.validId,
                    /**
                     * `idType` ו-`validType`, ולא `validType` בלבד.
                     *
                     * מה שנשמר על סשן ההרשמה נקרא `validType` (ראה `RegistrationData`
                     * ב-SSO), ומה שהטופס קורא נקרא `idType`. הכתיבה כאן הייתה ל-`validType`
                     * בלבד - שדה שאין לו קורא - ולכן לקוח שחזר לטופס אחרי אימות מייל קיבל
                     * את המספר שלו עם הסוג מאופס ל-`ID`, גם אם נרשם בדרכון.
                     */
                    idType: reply.data.data.validType ?? "ID",
                    validType: reply.data.data.validType, 
                    phoneCode: reply.data.data.phone.split(' ').length>0?reply.data.data.phone.split(' ')[0]:'',
                    phoneNumber: reply.data.data.phone.split(' ').length>1?reply.data.data.phone.split(' ')[1]:'',
                });
            } else {
                if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                    errorMessage = apiErrorMessage(reply.data, t);
                }
                if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                    setState({
                        ...state,
                        errors: {server_error: errorMessage}
                    });
                }
            }
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage}
            });
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!validateForm()) {
            let errorMessage = DEFAULT_ERROR_TEXT;
            
            let config = isUserRegister?{'headers': {"token": userToken(), "lang": getLang()}}:{'headers': {"lang": getLang()}};
          
            // `effectiveIdType` ולא `state.idType`: אותו ערך שהמסך הציג ואימת. ראה שם.
            const data = {firstName: state.firstName.trim(), lastName: state.lastName.trim(), email: state.email.trim(), validId: +state.idNumber.trim(), validType: effectiveIdType, phone: state.phoneCode+' '+state.phoneNumber.trim(), communicationPref: state.communicationPref};

            // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
            const payload = await captcha.attach(data);
            // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
            if (!payload) {
                return;
            }
        
            try {
                const reply = await submit.run(() => axios.post(API_URL + 'registerNewUser', payload, config));
                // A blocked second click returns nothing; the first one is still running.
                if (!reply) {
                    return;
                }
                if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK
                        && reply.data.hasOwnProperty('token')){
                    dispatch(register({token : reply.data.token}));
                    navigate("/verify-email");
                }else{
                    if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                        errorMessage = apiErrorMessage(reply.data, t);
                    }
                    if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                        setState({
                            ...state,
                            errors: {server_error: errorMessage}
                        });
                    }
                };
            } catch (e) {
                setState({
                    ...state,
                    errors: {server_error: errorMessage}
                });
            }
        }

    };

    const handleChange = (e) => {
        delete state.errors[e.target.id];
        setState({
            ...state,
            [e.target.id]: e.target.value,
            errors: {...state.errors},
        });
    };


    const toggleCommunicationPref = (e) => {
        setState({
            ...state,
            communicationPref: state.communicationPref === 'SMS'?'VOICE':'SMS',
        });
    };


    const setIdType = (idType) => {
        // המספר מתאפס עם הסוג: מספר שהוקלד כת.ז. אינו מספר דרכון, והשארה שלו בשדה
        // מותירה על המסך ערך שנבדק מול כלל אחר ממה שהלקוח רואה.
        setState({
            ...state,
            idType: idType,
            idNumber: "",
            errors: {},
        });
    };

    const toggleShowPhoneCodeDropdown = (e) => {
        setState({
            ...state,
            showPhoneCodeDropdown: !state.showPhoneCodeDropdown,
        });
    };

    const setPhoneCode = (phoneCode) => {
        setState({
            ...state,
            phoneCode: phoneCode,
            showPhoneCodeDropdown: false,
        });
    };

    const validateForm = () => {
        const errors = {};
        if (!state.firstName.trim() /*|| state.firstName.trim().length < 4*/) {
            errors['firstName'] = 'First Name is required';
        }

        if (!state.lastName.trim() /*|| state.firstName.trim().length < 4*/) {
            errors['lastName'] = 'Last Name is required';
        }

        if (!state.email.trim()) {
            errors['email'] = t('Email is required');
        } else if (!/\S+@\S+\.\S+/.test(state.email)) {
            errors['email'] = 'Email is invalid';
        }

        /**
         * מספר הזיהוי, לפי הסוג שנבחר.
         *
         * מה שהיה כאן היה שבור בשלוש דרכים, וכולן מאותו שורש - הרכבת מפתח תרגום
         * מהערך: `t(state.idType+' Number is required')` ייצר `"COM Number is
         * required"`, מפתח שאינו קיים בטבלה ולכן הודעת השגיאה הייתה `COM Number is
         * required` באנגלית על מסך עברי. התנאי השני בדק `'Passport'` באות גדולה -
         * ערך שהמסך אינו שולח - ולכן דרכון לא נבדק בכלל. וספרת הביקורת לא נבדקה
         * לאף סוג, גם לא לת.ז.
         *
         * עכשיו: מפתח מלא לכל סוג (`ID_NUMBER_REQUIRED` וכו'), ובדיקת ספרת ביקורת
         * לסוגים שיש להם אחת - ת.ז. **וח.פ.**, באותו אלגוריתם. ראה
         * `isValidIsraeliIdNumber`.
         */
        const idTypeSpec = ID_TYPE_BY_VALUE[effectiveIdType];
        if (!state.idNumber.trim()) {
            errors['idNumber'] = `${effectiveIdType}_NUMBER_REQUIRED`;
        } else if (idTypeSpec?.checkDigit) {
            if (!isValidIsraeliIdNumber(state.idNumber)) {
                errors['idNumber'] = `${effectiveIdType}_NUMBER_INVALID`;
            }
        } else if (!/^[\d -]*$/.test(state.idNumber) ||
                   state.idNumber.replace(/\D/g, '').length < 5 ||
                   state.idNumber.replace(/\D/g, '').length > 9) {
            /**
             * לדרכון אין ספרת ביקורת ואין צורה קבועה, אבל **ספרות בלבד** - וזה לא
             * החמרה אסתטית: `validId` הוא מספר בכל השרשרת, עד העמודה
             * `customers.valid_id`. `+'AB12345'` הוא `NaN`, כלומר דרכון עם אותיות
             * היה נשלח לשרת כלא-מספר. מה שכן אפשר לדרוש הוא חמש עד תשע ספרות.
             */
            errors['idNumber'] = 'PASSPORT_NUMBER_INVALID';
        }

        if (!state.phoneNumber.trim()) {
            errors['phoneNumber'] = 'Phone Number is required';
        } else if (!/^(?=(?:[^0-9]*[0-9]){7})[\d -]*$/.test(state.phoneNumber)) {
            errors['phoneNumber'] = 'Phone number is invalid';
        }

        setState({
            ...state,
            errors: errors,
        });

        return Object.keys(errors).length > 0;
    };


    /*const hasErrors = () => {
        return Object.keys(state.errors).length > 0;
    }*/


    /**
     * Enter ממשיך לשלב הבא. ראה `useEnterSubmit` - הפעולה הראשית של המסך אינה
     * כפתור שליחה אלא `div`, ולכן הדפדפן אינו שולח כאן דבר מעצמו.
     */
    useEnterSubmit((e) => handleSubmit(e), !submit.busy);

    return (
            <Frame>
                <div className='main main-register'>
                    <div className='main-top'>
                        <h1>{t("Create a new account")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <form onSubmit={handleSubmit}> 
                                <h2>{tBrand(t, "We are excited to welcome you to the Yemot Hamashiach family.", reseller)}</h2>
                                <h2>{t("Please insert the following details")}</h2>
                                {/*
                                    **הדרך חזרה להתחברות - קישור, ולא משפט.**

                                    השורה הזו הייתה כאן מאז ומתמיד וגם אמרה "יש לך
                                    חשבון? התחבר", אבל היא הייתה `h2` בלי `onClick` -
                                    כלומר טקסט שמצביע על פעולה ואינו מבצע אותה. מי
                                    שהגיע לכאן בטעות היה צריך את כפתור החזרה של
                                    הדפדפן, ואם הוא נחת ישירות על `/register` - לא
                                    הייתה לו דרך חזרה בכלל.
                                */}
                                <h2 className='teal pointer' onClick={() => navigate('/login')}><Trans i18nKey="Existing customer? <strong>Please login</strong>" /></h2>
                                <div className='mt30 input blue-border'> <input className={state.errors.firstName?"error":""} type="text" id="firstName" value={state.firstName} placeholder={t("First name")} onChange={(e) => handleChange(e)} /></div>
                                <div className='input blue-border'> <input className={state.errors.lastName?"error":""} type="text" id="lastName" value={state.lastName} placeholder={t("Last name")} onChange={(e) => handleChange(e)} /></div>

                                {/*
                                    בורר סוג הזיהוי - **גלוי, ולא מוסתר מאחורי חץ.**

                                    עד כאן הוא היה תפריט שנפתח מלחיצה על סמל חץ קטן בתוך
                                    שדה המספר, כלומר לא היה שום דבר על המסך שאומר שיש
                                    בכלל בחירה: מי שנרשם בדרכון או בח.פ. לא ידע שאפשר.
                                    עכשיו שלושת הסוגים (או אלה שהריסיילר פתח) הם שורת
                                    לחצנים עם כותרת, ושדה המספר מתחתם משנה את התווית שלו.

                                    מוצג רק כשיש יותר מסוג אחד פתוח - ראה `showIdTypePicker`.
                                */}
                                {showIdTypePicker && (
                                    <>
                                        <h2 className='teal mt30'>{t("What identification number will you register with?")}</h2>
                                        <div className='id-type-row'>
                                            {allowedIdTypes.map((type) => (
                                                <div
                                                    key={type.value}
                                                    className={`id-type-option${effectiveIdType === type.value ? ' id-type-option-selected' : ''}`}
                                                    onClick={() => setIdType(type.value)}
                                                >
                                                    {t(type.pickerLabel)}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {allowedIdTypes.length === 0 ? (
                                    /*
                                        ריסיילר שסגר את שלושת הסוגים סגר בפועל את ההרשמה.
                                        נאמר במפורש, במקום להציג טופס שהשרת ידחה.
                                    */
                                    <h2 className='mt30 error'>{t("Self registration is not available here right now.")}</h2>
                                ) : (
                                    <div className='input blue-border'>
                                        <input
                                            className={state.errors.idNumber?"error":""}
                                            type="text"
                                            id="idNumber"
                                            value={state.idNumber}
                                            placeholder={t(ID_TYPE_BY_VALUE[effectiveIdType]?.label ?? 'ID number')}
                                            onChange={(e) => handleChange(e)}
                                        />
                                    </div>
                                )}

                                <div className='phone-number'>
                                    <div className='ml15 input blue-border'>
                                        <input className={state.errors.phoneNumber?"error":""} type="tel" id="phoneNumber" value={state.phoneNumber} pattern="[\d\s\-]{5,10}" placeholder={t("Mobile phone number")} onChange={(e) => handleChange(e)} />
                                    </div>

                                    <div className='input blue-border relative phone-code-input'>
                                        <div className='phone-code mr10'><div className='ltr'>{state.phoneCode}</div> <img src={'/images/flags/flag-'+phoneCodes[state.phoneCode]+'.png'} className='flag-icon' alt=''/> </div>
                                        <div className='open-dropdown-icon-div open-phone-code-dropdown' onClick={(e) => toggleShowPhoneCodeDropdown(e)}><img src='/images/open-dropdown-icon.png' className='open-dropdown-icon' alt=''/></div>
                                        {state.showPhoneCodeDropdown?<div className="dropdown phone-code-dropdown  blue-border">
                                            {Object.entries(phoneCodes).map(([code, name]) => (
                                                <div key={code} className='item' onClick={(e) => setPhoneCode(code)}>
                                                    <div>{t(name)}</div>
                                                    <div className='phone-code'><img src={'/images/flags/flag-'+name+'.png'} className='flag-icon' alt=''/> <div className='ltr'>{code}</div> </div>
                                                </div>
                                            ))}
                                        </div>:<></>}
                                    </div>
                                </div>

                                <div className='input blue-border'> <input className={state.errors.email?"error":""} type="email" inputMode="email" id="email" value={state.email} placeholder={t("Email address")} onChange={(e) => handleChange(e)} /></div>

                                <h2 className='teal mt30'>{t("How do you prefer to communicate?")}</h2>
                                <div className='contact-type'>
                                    <h2 className='teal ml20'>{t("Voice call")}</h2>
                                    <div className={state.communicationPref==='SMS'?'switch switch-selected':'switch'} onClick={(e) => toggleCommunicationPref(e)}>
                                        <div className='switch-item'></div>
                                    </div>
                                    <h2 className='teal mr30'>{t("sms")}</h2>
                                </div>

                                {captcha.node}
                                <div {...submit.buttonProps({base: 'button button-space-between button-teal'})} onClick={(e) => handleSubmit(e)}>  <div className='mr20'>{submit.busy ? t("Creating your account...") : t("next")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>

                                <FormErrors errors={state.errors} />
                            </form>
                        </div>

                        <div className='left-main'>
                            <img src='/images/person-pointing.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
    );
}